/**
 * Обзор карточек макета — первый экран вкладки.
 *
 * Основной сценарий здесь просмотр, а не одобрение: у макета бывает по два
 * десятка карточек (пять артикулов на две площадки в нескольких кабинетах), и
 * прежняя вкладка выкладывала их простынёй, где каждая тянула за собой полный
 * снимок ревизии. Таблица показывает все сразу, а подробности раскрываются на
 * месте — без ухода на отдельный экран и с работающим поиском по странице.
 */

import React from 'react'
import { ChevronRight, ExternalLink, Send } from 'lucide-react'
import { Badge } from '../../../../components/ui/Badge'
import { Button } from '../../../../components/ui/Button'
import { assertNever, MARKETPLACES } from '../../../../shared/marketplaces'
import { ymStatusLabel } from '../../../yandex-market/catalog/columns'
import { SafeImg } from './parts'
import type { MarketplaceCardDto, MarketplaceFieldLabel, MarketplaceFieldLabels } from '../../../pipeline/api'
import type { PhotoResendInput } from '../../../../components/marketplace/photoResendApi'
import { MarketplacePhotos } from './MarketplacePhotos'
import { MarketplaceReasons } from './MarketplaceReasonList'
import {
  cardPhotos,
  cardPrice,
  cardStock,
  hasWarning,
  isBlocked,
  rowBadge,
  storefrontUrl,
} from './marketplaceCardState'

export type OverviewGrouping = 'cab' | 'article' | 'none'
export type OverviewFilter = 'all' | 'problem' | 'wb' | 'ozon' | 'ym'

const WB_TITLE_LIMIT = 60

const money = (value?: number | null) =>
  value == null ? '—' : `${Number(value).toLocaleString('ru-RU')} ₽`

const plural = (n: number, one: string, few: string, many: string) => {
  const mod100 = n % 100
  const mod10 = n % 10
  if (mod100 >= 11 && mod100 <= 14) return `${n} ${many}`
  if (mod10 === 1) return `${n} ${one}`
  if (mod10 >= 2 && mod10 <= 4) return `${n} ${few}`
  return `${n} ${many}`
}

const cabinetName = (card: MarketplaceCardDto) =>
  card.seller_name || `Кабинет ${card.seller_id}`

/**
 * `MarketplaceCardDto['marketplace']` (`features/pipeline/api.ts`) — три
 * площадки конвейера карточек: `wb`, `ozon`, `ym` (Этап 2,
 * `docs/SPEC_2026-09-13_YANDEX_MARKET.md`, §4). `switch` с `assertNever`
 * вместо тернарника — если тип когда-нибудь расширится, компилятор сразу
 * укажет каждое место, которого это касается, вместо того чтобы молча увести
 * новую площадку в ветку OZON.
 */
type OverviewMarketplace = MarketplaceCardDto['marketplace']

function overviewBadgeTone(code: OverviewMarketplace): 'purple' | 'blue' | 'yellow' | 'gray' {
  switch (code) {
    case 'wb': return 'purple'
    case 'ozon': return 'blue'
    case 'ym': return 'yellow'
    // Сайт — канал, а не площадка, но его карточки бэкенд отдаёт сюда же.
    case 'site': return 'gray'
    default: return assertNever(code)
  }
}

function overviewShortLabel(code: OverviewMarketplace): string {
  switch (code) {
    case 'wb': return 'WB'
    case 'ozon': return 'OZON'
    case 'ym': return MARKETPLACES.ym.label
    case 'site': return 'Сайт'
    default: return assertNever(code)
  }
}

/**
 * Факты, которые чаще всего сверяют, не открывая карточку целиком.
 *
 * У ym пока пусто: справочник характеристик `mp_characteristic_mappings` для
 * Маркета заводит задача 2C того же этапа, а свои опознавательные факты
 * (SKU Маркета, категория, статус карточки) у ym не значения справочника, а
 * отдельные поля — их рисует `YmIdentityFacts` ниже, не через этот справочник.
 */
const QUICK_FACTS: Record<'wb' | 'ozon' | 'ym', string[]> = {
  wb: ['weight_net', 'shelf_life', 'country_of_origin', 'declaration_number'],
  ozon: ['weight_net', 'shelf_life_days', 'country', 'composition'],
  ym: [],
}

interface Props {
  cards: MarketplaceCardDto[]
  fieldLabels: MarketplaceFieldLabels | null
  grouping: OverviewGrouping
  filter: OverviewFilter
  expandedKey: string | null
  onGrouping: (value: OverviewGrouping) => void
  onFilter: (value: OverviewFilter) => void
  onToggle: (key: string) => void
  onOpenCard: (key: string) => void
  onCompare: (key: string) => void
  onOpenVisual: (urls: string[], index: number, article: string) => void
  /** Код макета — вход «Переотправить все фото макета». */
  designCode?: string
  /** Показывать чекбоксы и кнопки переотправки фото — право `Card:UPDATE`. */
  canResendPhotos?: boolean
  /** Открыть общую модалку `PhotoResendModal` с готовым входом. */
  onResendPhotos?: (input: PhotoResendInput) => void
}

export const cardKey = (card: MarketplaceCardDto) =>
  `${card.marketplace}-${card.seller_id}-${card.article}`

/**
 * «Опубликована» для чекбокса переотправки фото — та же граница, что и
 * бэкенд (`PUBLISHED_CARD_CONDITIONS` в
 * `services/marketplace/src/services/marketplace-source-change-scope.service.ts`):
 * есть ID на площадке. Точное совпадение статусов бэкенд перепроверит сам в
 * предпросмотре («нет кабинета»/«нет визуалов» и т.п.) — здесь достаточно
 * не предлагать выбрать заведомо неопубликованную строку.
 */
function isPublishedForResend(card: MarketplaceCardDto): boolean {
  return Boolean(card.external_id && String(card.external_id).trim())
}

export const MarketplaceOverview: React.FC<Props> = ({
  cards,
  fieldLabels,
  grouping,
  filter,
  expandedKey,
  onGrouping,
  onFilter,
  onToggle,
  onOpenCard,
  onCompare,
  onOpenVisual,
  designCode,
  canResendPhotos = false,
  onResendPhotos,
}) => {
  const [selected, setSelected] = React.useState<Set<string>>(() => new Set())

  const toggleSelected = (key: string) => {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const counts = {
    all: cards.length,
    problem: cards.filter((card) => isBlocked(card) || hasWarning(card)).length,
    wb: cards.filter((card) => card.marketplace === 'wb').length,
    ozon: cards.filter((card) => card.marketplace === 'ozon').length,
    ym: cards.filter((card) => card.marketplace === 'ym').length,
  }

  const visible = cards.filter((card) => {
    if (filter === 'problem') return isBlocked(card) || hasWarning(card)
    if (filter === 'wb') return card.marketplace === 'wb'
    if (filter === 'ozon') return card.marketplace === 'ozon'
    if (filter === 'ym') return card.marketplace === 'ym'
    return true
  })

  const groups = new Map<string, MarketplaceCardDto[]>()
  for (const card of visible) {
    const key =
      grouping === 'cab'
        ? `${card.marketplace}|${cabinetName(card)}`
        : grouping === 'article'
          ? card.article
          : 'all'
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(card)
  }

  const baseColumns = grouping === 'cab' ? 5 : 6
  const columns = canResendPhotos ? baseColumns + 1 : baseColumns

  const publishableVisible = visible.filter(isPublishedForResend)
  const allPublishableSelected =
    publishableVisible.length > 0 && publishableVisible.every((card) => selected.has(cardKey(card)))
  const toggleSelectAll = (checked: boolean) => {
    setSelected((current) => {
      const next = new Set(current)
      for (const card of publishableVisible) {
        if (checked) next.add(cardKey(card))
        else next.delete(cardKey(card))
      }
      return next
    })
  }
  const selectedCards = visible.filter((card) => selected.has(cardKey(card)))

  const resendSelected = () => {
    if (selectedCards.length === 0) return
    onResendPhotos?.({
      cards: selectedCards.map((card) => ({
        article: card.article,
        marketplace: card.marketplace,
        seller_id: card.seller_id,
      })),
    })
  }
  const resendAllForDesign = () => {
    if (!designCode) return
    onResendPhotos?.({ design_code: designCode })
  }

  return (
    <div className="space-y-2.5">
      {canResendPhotos && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-800/60">
          <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-zinc-300">
            <input
              type="checkbox"
              checked={allPublishableSelected}
              disabled={publishableVisible.length === 0}
              onChange={(event) => toggleSelectAll(event.target.checked)}
              aria-label="Выбрать все опубликованные карточки"
              className="h-3.5 w-3.5 accent-indigo-600"
            />
            Выбрать все
          </label>
          <Button
            size="sm"
            icon={<Send className="h-3.5 w-3.5" />}
            disabled={selectedCards.length === 0}
            onClick={resendSelected}
          >
            Переотправить фото ({selectedCards.length})
          </Button>
          <Button size="sm" variant="secondary" disabled={!designCode} onClick={resendAllForDesign}>
            Переотправить все фото макета
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-lg border border-gray-200 px-2 py-1 dark:border-zinc-700">
          <span className="text-2xs text-gray-400 dark:text-zinc-500">Группировать</span>
          {([
            ['cab', 'по кабинету'],
            ['article', 'по товару'],
            ['none', 'без групп'],
          ] as Array<[OverviewGrouping, string]>).map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={grouping === value}
              onClick={() => onGrouping(value)}
              className={`rounded px-2 py-0.5 text-xs ${
                grouping === value
                  ? 'bg-indigo-50 font-semibold text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300'
                  : 'text-gray-500 dark:text-zinc-400'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {([
          ['all', 'Все'],
          ['problem', 'С проблемами'],
          ['wb', 'WB'],
          ['ozon', 'OZON'],
          ['ym', MARKETPLACES.ym.label],
        ] as Array<[OverviewFilter, string]>).map(([value, label]) => (
          <button
            key={value}
            type="button"
            aria-pressed={filter === value}
            onClick={() => onFilter(value)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
              filter === value
                ? 'border-indigo-200 bg-indigo-50 font-semibold text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300'
                : 'border-gray-200 text-gray-600 dark:border-zinc-700 dark:text-zinc-300'
            }`}
          >
            {label}
            <span className="text-2xs text-gray-400 dark:text-zinc-500">{counts[value]}</span>
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-zinc-800">
        <table className="w-full table-fixed border-collapse text-[0.8125rem]">
          <colgroup>
            {canResendPhotos && <col className="w-8" />}
            <col className="w-11" />
            <col className="w-36" />
            {grouping !== 'cab' && <col className="w-28" />}
            <col className="w-40" />
            <col />
            <col className="w-16" />
          </colgroup>
          <thead>
            <tr className="bg-gray-50 text-2xs uppercase tracking-wide text-gray-400 dark:bg-zinc-900 dark:text-zinc-500">
              {canResendPhotos && <th className="px-2 py-1.5" />}
              <th className="px-2 py-1.5" />
              <th className="px-2 py-1.5 text-left font-bold">Артикул</th>
              {grouping !== 'cab' && <th className="px-2 py-1.5 text-left font-bold">Площадка</th>}
              <th className="px-2 py-1.5 text-left font-bold">Состояние</th>
              <th className="px-2 py-1.5 text-left font-bold">Наименование</th>
              <th className="px-2 py-1.5 text-right font-bold">Длина</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr>
                <td colSpan={columns} className="px-3 py-6 text-center text-xs text-gray-500 dark:text-zinc-400">
                  В этом фильтре карточек нет.
                </td>
              </tr>
            )}

            {[...groups.entries()].map(([key, items]) => {
              const first = items[0]
              const bad = items.filter(isBlocked).length
              const warn = items.filter((card) => !isBlocked(card) && hasWarning(card)).length
              return (
                <React.Fragment key={key}>
                  {grouping !== 'none' && (
                    <tr className="border-t border-gray-200 bg-gray-50 dark:border-zinc-800 dark:bg-zinc-900">
                      <td colSpan={columns} className="px-2.5 py-1.5">
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                          {grouping === 'cab' ? (
                            <>
                              <Badge variant={overviewBadgeTone(first.marketplace)}>
                                {overviewShortLabel(first.marketplace)}
                              </Badge>
                              <b>{cabinetName(first)}</b>
                            </>
                          ) : (
                            <>
                              <b className="font-mono">{first.article}</b>
                              <span className="text-gray-500 dark:text-zinc-400">
                                {first.revision_snapshot?.category_prefix || first.category_prefix || ''}
                              </span>
                            </>
                          )}
                          <span className="ml-auto text-2xs text-gray-400 dark:text-zinc-500">
                            {plural(items.length, 'карточка', 'карточки', 'карточек')}
                          </span>
                          {bad > 0 ? (
                            <Badge variant="red">{bad} не уедет</Badge>
                          ) : warn > 0 ? (
                            <Badge variant="yellow">{warn} с замечаниями</Badge>
                          ) : (
                            <Badge variant="green">все в порядке</Badge>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}

                  {items.map((card) => {
                    const key = cardKey(card)
                    const photos = cardPhotos(card)
                    const badge = rowBadge(card)
                    const title = card.revision_snapshot?.title || card.title || null
                    const overLimit =
                      card.marketplace === 'wb' && !!title && title.length > WB_TITLE_LIMIT
                    const expanded = expandedKey === key
                    return (
                      <React.Fragment key={key}>
                        <tr
                          tabIndex={0}
                          aria-expanded={expanded}
                          onClick={() => onToggle(key)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault()
                              onOpenCard(key)
                            }
                          }}
                          className={`cursor-pointer border-t border-gray-200 dark:border-zinc-800 ${
                            expanded
                              ? 'bg-indigo-50/60 dark:bg-indigo-950/30'
                              : 'hover:bg-gray-50 dark:hover:bg-zinc-900'
                          }`}
                        >
                          {canResendPhotos && (
                            <td className="py-1.5 pl-2.5 pr-1" onClick={(event) => event.stopPropagation()}>
                              {isPublishedForResend(card) && (
                                <input
                                  type="checkbox"
                                  checked={selected.has(key)}
                                  onChange={() => toggleSelected(key)}
                                  aria-label={`Выбрать ${card.article} (${overviewShortLabel(card.marketplace)}) для переотправки фото`}
                                  className="h-3.5 w-3.5 accent-indigo-600"
                                />
                              )}
                            </td>
                          )}
                          <td className="py-1.5 pl-2.5 pr-1">
                            {photos.length > 0 ? (
                              <span className="block h-9 w-7 overflow-hidden rounded border border-gray-200 dark:border-zinc-700">
                                <SafeImg
                                  src={photos[0]}
                                  alt=""
                                  className="h-full w-full object-cover"
                                  iconClass="h-3 w-3"
                                />
                              </span>
                            ) : (
                              <span
                                title="Фото нет"
                                className="flex h-9 w-7 items-center justify-center rounded border border-dashed border-red-300 text-2xs text-red-500 dark:border-red-900"
                              >
                                —
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1.5">
                            <span className="block font-mono text-xs font-semibold">{card.article}</span>
                            <span className="text-2xs text-gray-500 dark:text-zinc-400">
                              {money(cardPrice(card))}
                            </span>
                          </td>
                          {grouping !== 'cab' && (
                            <td className="px-2 py-1.5">
                              <Badge variant={overviewBadgeTone(card.marketplace)}>
                                {overviewShortLabel(card.marketplace)}
                              </Badge>
                              <span className="mt-0.5 block truncate text-2xs text-gray-500 dark:text-zinc-400">
                                {cabinetName(card)}
                              </span>
                            </td>
                          )}
                          <td className="px-2 py-1.5">
                            <Badge variant={badge.variant}>{badge.label}</Badge>
                          </td>
                          <td className="px-2 py-1.5">
                            <span className="line-clamp-2 leading-snug">{title || '—'}</span>
                          </td>
                          <td className="px-2 py-1.5 text-right text-2xs tabular-nums">
                            {title ? (
                              <span
                                className={
                                  overLimit
                                    ? 'font-bold text-red-600 dark:text-red-400'
                                    : 'text-gray-400 dark:text-zinc-500'
                                }
                              >
                                {title.length}
                                {card.marketplace === 'wb' ? ` / ${WB_TITLE_LIMIT}` : ''}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>

                        {expanded && (
                          <tr className="border-t border-dashed border-gray-200 bg-gray-50 dark:border-zinc-800 dark:bg-zinc-900/60">
                            <td colSpan={columns} className="px-3 py-3">
                              <ExpandedRow
                                card={card}
                                fieldLabels={fieldLabels}
                                onOpenVisual={onOpenVisual}
                                onOpenCard={() => onOpenCard(key)}
                                onCompare={() => onCompare(key)}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      <p className="text-2xs leading-relaxed text-gray-400 dark:text-zinc-500">
        Клик по строке разворачивает фото и текст прямо здесь, Enter открывает карточку целиком.
      </p>
    </div>
  )
}

/**
 * Быстрые факты ym: SKU Маркета, категория Маркета, статус карточки.
 *
 * Не через `QUICK_FACTS`/`field_values`: это не значения справочника
 * характеристик площадки, а собственные опознавательные поля карточки —
 * `market_sku` назначает модерация, категория — замороженный
 * `marketplace_category.ym_category_id`, статус — реальный `cardStatus`
 * площадки (9 значений, словарь `features/yandex-market/catalog/columns.tsx`).
 */
const YmIdentityFacts: React.FC<{ card: MarketplaceCardDto }> = ({ card }) => {
  const category = card.revision_snapshot?.marketplace_category as Record<string, unknown> | null
  const categoryId = category?.ym_category_id
  const rows: Array<{ key: string; label: string; value: string }> = [
    { key: 'market_sku', label: 'SKU Маркета', value: card.external_id ? String(card.external_id) : 'не назначен' },
    { key: 'ym_category', label: 'Категория Маркета', value: categoryId ? String(categoryId) : 'не заполнена' },
    { key: 'card_status', label: 'Статус карточки', value: ymStatusLabel(card.status) },
  ]
  return (
    <dl className="grid grid-cols-1 gap-x-5 sm:grid-cols-2">
      {rows.map((row) => (
        <div
          key={row.key}
          className="flex items-baseline justify-between gap-3 border-b border-dashed border-gray-200 py-1 text-2xs dark:border-zinc-800"
        >
          <dt className="text-gray-400 dark:text-zinc-500">{row.label}</dt>
          <dd className="text-right text-gray-600 dark:text-zinc-300">{row.value}</dd>
        </div>
      ))}
    </dl>
  )
}

/** Раскрытая строка: сначала фото — их и проверяют, — затем текст и факты. */
const ExpandedRow: React.FC<{
  card: MarketplaceCardDto
  fieldLabels: MarketplaceFieldLabels | null
  onOpenVisual: (urls: string[], index: number, article: string) => void
  onOpenCard: () => void
  onCompare: () => void
}> = ({ card, fieldLabels, onOpenVisual, onOpenCard, onCompare }) => {
  const [full, setFull] = React.useState(false)
  const photos = cardPhotos(card)
  const snapshot = card.revision_snapshot
  const title = snapshot?.title || card.title || null
  const description = snapshot?.description || null
  const values = snapshot?.field_values ?? {}
  const cardLabels: MarketplaceFieldLabel[] = card.marketplace === 'site'
    ? []
    : fieldLabels?.[card.marketplace] ?? []
  const labels = new Map(cardLabels.map((item) => [item.key, item]))
  // У канала сайта своих характеристик площадки нет — быстрых фактов тоже.
  const facts = (card.marketplace === 'site' ? ([] as string[]) : QUICK_FACTS[card.marketplace])
    .map((key) => ({ key, label: labels.get(key), value: values[key] }))
    .filter((row) => row.value !== undefined && row.label)
  const url = storefrontUrl(card)

  return (
    <div className="space-y-3">
      <MarketplacePhotos urls={photos} article={card.article} onOpen={onOpenVisual} />

      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant="gray">{money(cardPrice(card))}</Badge>
        {cardStock(card) != null && (
          <Badge variant="gray">Плановый остаток: {cardStock(card)}</Badge>
        )}
        <Badge variant="gray">{cabinetName(card)}</Badge>
        <Badge variant="gray">{plural(photos.length, 'фото', 'фото', 'фото')}</Badge>
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}
            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline dark:text-indigo-400"
          >
            {card.marketplace === 'wb' ? 'nmID' : 'SKU'} {card.external_id}
            <ExternalLink className="h-3 w-3" />
          </a>
        ) : (
          // У ym внешней ссылки нет вовсе (формат витрины Маркета не подтверждён,
          // §5 SPEC_2026-09-13_YANDEX_MARKET) — идентификатор показываем текстом.
          card.marketplace === 'ym' && card.external_id && (
            <Badge variant="gray">SKU Маркета: {card.external_id}</Badge>
          )
        )}
      </div>

      {title && <p className="text-sm font-semibold leading-snug">{title}</p>}

      {description ? (
        <div>
          <p
            className={`whitespace-pre-wrap border-l-2 border-gray-300 pl-2.5 text-xs leading-6 text-gray-600 dark:border-zinc-600 dark:text-zinc-300 ${
              full ? '' : 'line-clamp-3'
            }`}
          >
            {description}
          </p>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              setFull(!full)
            }}
            className="mt-1 text-2xs text-indigo-600 underline underline-offset-2 dark:text-indigo-400"
          >
            {full ? 'свернуть описание' : 'показать описание целиком'}
          </button>
        </div>
      ) : (
        <p className="text-2xs text-gray-400 dark:text-zinc-500">
          Описание хранится в замороженной ревизии и на витрине — в синхронизированном каталоге его
          нет.
        </p>
      )}

      {facts.length > 0 && (
        <dl className="grid grid-cols-1 gap-x-5 sm:grid-cols-2">
          {facts.map((row) => (
            <div
              key={row.key}
              className="flex items-baseline justify-between gap-3 border-b border-dashed border-gray-200 py-1 text-2xs dark:border-zinc-800"
            >
              <dt className="text-gray-400 dark:text-zinc-500">{row.label?.name}</dt>
              <dd className="text-right text-gray-600 dark:text-zinc-300">
                {row.value === null || row.value === ''
                  ? <span className="text-red-600 dark:text-red-400">не заполнено</span>
                  : String(row.value)}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {card.marketplace === 'ym' && <YmIdentityFacts card={card} />}

      <MarketplaceReasons card={card} />

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            onOpenCard()
          }}
          className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:border-gray-300 dark:border-zinc-700 dark:text-zinc-300"
        >
          Открыть карточку целиком <ChevronRight className="h-3 w-3" />
        </button>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            onCompare()
          }}
          className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:border-gray-300 dark:border-zinc-700 dark:text-zinc-300"
        >
          Сравнить площадки
        </button>
      </div>
    </div>
  )
}

export default MarketplaceOverview
